import { useState, useEffect, useCallback } from "react";
import { SEOHead } from "@/components/SEOHead";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check } from "lucide-react";
import { CountdownTimer } from "@/components/CountdownTimer";
import { CheckoutStepBuyer, BuyerData } from "@/components/checkout/CheckoutStepBuyer";
import { CheckoutStepData } from "@/components/checkout/CheckoutStepData";
import { CheckoutStepPayment } from "@/components/checkout/CheckoutStepPayment";
import { CheckoutStepConfirmation } from "@/components/checkout/CheckoutStepConfirmation";
import { CheckoutStepHalfPriceDocs, HalfPriceDocTicket } from "@/components/checkout/CheckoutStepHalfPriceDocs";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getCheckoutQuestions, saveOrderProducts } from "@/lib/api-checkout";
import { isQuestionAvailableForTiers, validateQuestionOptions } from "@/lib/checkout-form";
import { createPayment, CreditCardData, getInstallmentOptions } from "@/lib/api-payment";
import { normalizePhoneDigits } from "@/lib/validators";
import { toast } from "@/hooks/use-toast";
import { formatBRL } from "@/lib/utils";
import { storageKeys } from "@/lib/storage";
import { trackPurchase } from "@/lib/api";
import { formatEventDateTimeRange, resolveEventTimeZone } from "@/lib/event-timezone";
import { useTranslation } from "react-i18next";
import { FiscalCheckoutPanel } from '@/components/fiscal/national/FiscalCheckoutPanel';

/** Dígitos de telefone ou null se vazio (gravação em orders/tickets/profiles). */
function digitsPhoneOrNull(value: string | null | undefined): string | null {
  const d = normalizePhoneDigits(value);
  return d || null;
}

export default function Checkout() {
  const { t } = useTranslation("checkout");
  const stepsList = [t("checkout.steps.buyer"), t("checkout.steps.attendees"), t("checkout.steps.payment"), t("checkout.steps.confirmation")];
  const { user } = useAuth();
  const orderStorageKey = storageKeys.checkoutOrderId(user?.id ?? null);

  const [step, setStep] = useState(0);
  const [fiscalReady, setFiscalReady] = useState(true);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(() => {
    // Recover orderId from sessionStorage on refresh
    return sessionStorage.getItem(orderStorageKey);
  });
  const [isRestoringOrder, setIsRestoringOrder] = useState(true);
  const [orderExpiresAt, setOrderExpiresAt] = useState<string | null>(null);
  const [paymentCreated, setPaymentCreated] = useState(false);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [pixQrCode, setPixQrCode] = useState<string | null>(null);
  const [pixQrCodeImage, setPixQrCodeImage] = useState<string | null>(null);
  const [boletoUrl, setBoletoUrl] = useState<string | null>(null);
  const [boletoBarcode, setBoletoBarcode] = useState<string | null>(null);
  const [showHalfPriceDocs, setShowHalfPriceDocs] = useState(false);
  const [halfPriceTickets, setHalfPriceTickets] = useState<HalfPriceDocTicket[]>([]);
  const [pendingFinalize, setPendingFinalize] = useState<(() => Promise<void>) | null>(null);
  /** Preview do total no cartão (juros/parcelas) — atualizado pelo step de pagamento */
  const [paymentPreview, setPaymentPreview] = useState<{
    method: string;
    installments: number;
    chargeTotal: number;
    interestAmount: number;
  } | null>(null);

  const { items, subtotal, platformFee, expiresAt, clearCart, discount, finalTotal, trackingCode, couponCode } = useCart();
  const navigate = useNavigate();

  const sidebarInterest =
    step === 2 && paymentPreview?.method === "credit_card"
      ? paymentPreview.interestAmount
      : 0;
  const sidebarChargeTotal =
    step === 2 && paymentPreview?.method === "credit_card"
      ? paymentPreview.chargeTotal
      : finalTotal;
  const sidebarInstallments =
    step === 2 && paymentPreview?.method === "credit_card"
      ? paymentPreview.installments
      : 1;
  const sidebarInstallmentValue =
    sidebarInstallments > 1
      ? getInstallmentOptions(finalTotal).find((o) => o.n === sidebarInstallments)?.value
      : null;

  const [buyerData, setBuyerData] = useState<BuyerData>({
    fullName: "",
    email: "",
    birthDate: "",
    cpf: "",
    companyName: "",
    phone: "",
    cep: "",
    street: "",
    addressNumber: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
  });

  const [attendeeData, setAttendeeData] = useState<Record<string, { name: string; email: string; cpf: string; phone: string }>>({});
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});

  const eventIds = [...new Set(items.map((i) => i.eventId))];
  const eventId = eventIds[0];

  const { data: allQuestions = [] } = useQuery({
    queryKey: ["checkout-questions-all", eventIds],
    queryFn: async () => {
      const results = await Promise.all(eventIds.map((eid) => getCheckoutQuestions(eid)));
      return results.flat();
    },
    enabled: eventIds.length > 0,
  });

  const { data: eventDetails } = useQuery({
    queryKey: ["checkout-event-details", eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const { data } = await supabase
        .from("events")
        .select("title, start_date, venue_name, venue_city, is_online, time_zone")
        .eq("id", eventId)
        .single();
      return data;
    },
    enabled: !!eventId,
  });

  const eventTimeZone = resolveEventTimeZone(eventDetails?.time_zone);

  const orderQuestions = allQuestions.filter((q) => q.applies_to === "order");
  const attendeeQuestions = allQuestions.filter((q) => q.applies_to === "attendee");

  // Determine if this is a free order (skip payment step)
  const isFreeCart = finalTotal === 0;

  // Restore order state from sessionStorage on page load
  useEffect(() => {
    const storedOrderId = sessionStorage.getItem(orderStorageKey);
    if (!storedOrderId) {
      setIsRestoringOrder(false);
      return;
    }
    (async () => {
      try {
        const { data: order } = await supabase
          .from("orders")
          .select("status, expires_at")
          .eq("id", storedOrderId)
          .maybeSingle();
        if (!order || order.status !== "pending" || new Date(order.expires_at) <= new Date()) {
          sessionStorage.removeItem(orderStorageKey);
          setOrderId(null);
        }
      } catch {
        // ignore
      } finally {
        setIsRestoringOrder(false);
      }
    })();
  }, [orderStorageKey]);

  // Realtime subscription for payment confirmation
  useEffect(() => {
    if (!orderId || !awaitingPayment) return;

    const channel = supabase
      .channel(`order-${orderId}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "orders",
        filter: `id=eq.${orderId}`,
      }, (payload) => {
        const newStatus = (payload.new)?.status;
        if (newStatus === "paid") {
          toast({ title: t("checkout.toastsAndErrors.paymentConfirmed"), description: t("checkout.toastsAndErrors.ticketsGeneratedSuccess") });
          clearCart();
          setStep(3);
          sessionStorage.removeItem(orderStorageKey);
          setAwaitingPayment(false);
        } else if (newStatus === "cancelled" || newStatus === "expired") {
          toast({ title: t("checkout.toastsAndErrors.paymentNotApproved"), description: t("checkout.toastsAndErrors.tryAgain"), variant: "destructive" });
          setAwaitingPayment(false);
          setPaymentCreated(false);
        }
      })
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.warn("Realtime channel error for order:", orderId);
        }
      });

    return () => {
      channel.unsubscribe().catch(() => { });
      supabase.removeChannel(channel).catch(() => { });
    };
  }, [orderId, awaitingPayment, clearCart, orderStorageKey, t]);

  // Fallback polling for payment confirmation (every 10s) — catches webhook failures
  useEffect(() => {
    if (!orderId || !awaitingPayment) return;

    const interval = setInterval(async () => {
      try {
        const { data: order } = await supabase
          .from("orders")
          .select("status")
          .eq("id", orderId)
          .maybeSingle();

        if (order?.status === "paid") {
          toast({ title: t("checkout.toastsAndErrors.paymentConfirmed"), description: t("checkout.toastsAndErrors.ticketsGeneratedSuccess") });
          clearCart();
          setStep(3);
          sessionStorage.removeItem(orderStorageKey);
          setAwaitingPayment(false);
        } else if (order?.status === "cancelled" || order?.status === "expired") {
          toast({ title: t("checkout.toastsAndErrors.paymentNotApproved"), description: t("checkout.toastsAndErrors.tryAgain"), variant: "destructive" });
          setAwaitingPayment(false);
          setPaymentCreated(false);
        }
      } catch (err) {
        console.warn("[Checkout] Polling error:", err);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [orderId, awaitingPayment, clearCart, orderStorageKey, t]);

  // Realtime subscription to clear boleto data when the boleto is paid
  useEffect(() => {
    if (!orderId || (!boletoUrl && !boletoBarcode)) return;

    const channel = supabase
      .channel(`order-boleto-${orderId}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "orders",
        filter: `id=eq.${orderId}`,
      }, (payload) => {
        const newBoletoUrl = payload.new?.boleto_url;
        const newBoletoBarcode = payload.new?.boleto_barcode;
        if (!newBoletoUrl && !newBoletoBarcode) {
          setBoletoUrl(null);
          setBoletoBarcode(null);
        }
      })
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.warn("Realtime channel error for boleto order:", orderId);
        }
      });

    return () => {
      channel.unsubscribe().catch(() => { });
      supabase.removeChannel(channel).catch(() => { });
    };
  }, [orderId, boletoUrl, boletoBarcode]);

  // Finalize order after creation (and after half-price docs, if required): confirm free orders or go to payment
  const finalizeOrder = useCallback(async (newOrderId: string, isServerFree: boolean) => {
    if (isServerFree) {
      const { data: confirmed, error: confirmErr } = await supabase.rpc("confirm_order_payment", {
        p_order_id: newOrderId,
        p_asaas_payment: "free",
        p_net_value: 0,
      });
      if (confirmErr) {
        console.error("confirm_order_payment error:", confirmErr);
        await supabase.from("tickets").update({ status: "active" }).eq("order_id", newOrderId).eq("status", "reserved");
      } else if (confirmed === false) {
        toast({ title: t("checkout.toastsAndErrors.attention"), description: t("checkout.toastsAndErrors.orderProcessedError"), variant: "destructive" });
        return;
      }

      const { data: activeTickets } = await supabase
        .from("tickets")
        .select("id")
        .eq("order_id", newOrderId)
        .eq("status", "active");
      if (activeTickets?.length) {
        await Promise.all(
          activeTickets.map((t) =>
            supabase.functions.invoke("generate-qr-code", { body: { ticketId: t.id } }).catch(err => {
              console.error("Erro ao invocar generate-qr-code:", err);
            })
          )
        );

        try {
          const { error: emailError } = await supabase.functions.invoke("send-ticket-email", { body: { orderId: newOrderId } });
          if (emailError) {
            console.error("Failed to trigger email for free order in Checkout (API Error):", emailError);
          } else {
            console.log("Free order email trigger sent for order (Checkout path):", newOrderId);
          }
        } catch (e) {
          console.error("Failed to trigger email for free order in Checkout (Exception):", newOrderId, e);
        }
      }

      trackPurchase(newOrderId, 0, items);
      toast({ title: t("checkout.toastsAndErrors.registrationConfirmed"), description: t("checkout.toastsAndErrors.ticketsGeneratedSuccess") });
      setStep(3);
      clearCart();
      sessionStorage.removeItem(orderStorageKey);
    } else {
      setStep(2); // Go to payment
    }
  }, [items, clearCart, orderStorageKey, t]);

  /** Grava buyer_* no order e attendee_* nos tickets (dígitos no phone). */
  const persistBuyerAndAttendees = useCallback(
    async (targetOrderId: string) => {
      const buyerIsCnpj = buyerData.cpf.replace(/\D/g, "").length === 14;
      const cleanBuyerPhone = digitsPhoneOrNull(buyerData.phone);
      const { error: orderErr } = await supabase
        .from("orders")
        .update({
          buyer_name: buyerData.fullName,
          buyer_email: buyerData.email,
          buyer_phone: cleanBuyerPhone,
          buyer_document: buyerData.cpf || null,
          billing_company_name: buyerIsCnpj ? buyerData.companyName?.trim() || null : null,
        })
        .eq("id", targetOrderId);
      if (orderErr) {
        console.error("[Checkout] Falha ao gravar buyer no order:", orderErr);
        throw new Error(orderErr.message || t("checkout.toastsAndErrors.saveBuyerDataError"));
      }

      const { data: tickets, error: ticketsFetchErr } = await supabase
        .from("tickets")
        .select("id, tier_id, status")
        .eq("order_id", targetOrderId)
        .order("created_at");
      if (ticketsFetchErr) {
        console.error("[Checkout] Falha ao listar tickets:", ticketsFetchErr);
        throw new Error(ticketsFetchErr.message || t("checkout.toastsAndErrors.loadTicketsError"));
      }

      if (tickets?.length) {
        const tierIdx: Record<string, number> = {};
        const fallbackName = buyerData.fullName || user?.email || "Participante";
        const fallbackEmail = buyerData.email || user?.email || null;
        const fallbackPhone = cleanBuyerPhone;
        const fallbackCpf = buyerData.cpf || null;

        for (const ticket of tickets) {
          // Só reserved: policy RLS e dados ainda editáveis no checkout
          if (ticket.status && ticket.status !== "reserved") continue;
          const idx = tierIdx[ticket.tier_id] || 0;
          tierIdx[ticket.tier_id] = idx + 1;
          const key = `${ticket.tier_id}-${idx}`;
          const data = attendeeData[key];
          const attendeePhone =
            digitsPhoneOrNull(data?.phone) || fallbackPhone;
          const { data: updated, error: ticketUpdateErr } = await supabase
            .from("tickets")
            .update({
              attendee_name: data?.name?.trim() || fallbackName,
              attendee_email: data?.email?.trim() || fallbackEmail,
              attendee_cpf: data?.cpf?.trim() || fallbackCpf,
              attendee_phone: attendeePhone,
            })
            .eq("id", ticket.id)
            .eq("status", "reserved")
            .select("id");
          if (ticketUpdateErr) {
            console.error("[Checkout] Erro ao atualizar ticket:", ticket.id, ticketUpdateErr);
            throw new Error(ticketUpdateErr.message || t("checkout.toastsAndErrors.saveAttendeeDataError"));
          }
          if (!updated?.length) {
            console.error("[Checkout] Update de ticket sem linhas (RLS ou status):", ticket.id);
            throw new Error(t("checkout.toastsAndErrors.saveAttendeeDataErrorRetry"));
          }
        }
      }
    },
    [attendeeData, buyerData, user?.email, t],
  );

  // Create order using server-side RPC for price validation
  const handleCreateOrder = useCallback(async () => {
    if (!user) {
      toast({ title: t("checkout.toastsAndErrors.loginRequired"), description: t("checkout.toastsAndErrors.loginRequiredDesc"), variant: "destructive" });
      return;
    }
    if (orderId) {
      // Verify stored order is still valid before reusing — re-sincroniza phone/dados
      const { data: existingOrder } = await supabase
        .from("orders")
        .select("status, expires_at, total, payment_method")
        .eq("id", orderId)
        .maybeSingle();
      const orderStillValid =
        existingOrder?.status === "pending" &&
        (!existingOrder.expires_at || new Date(existingOrder.expires_at) > new Date());
      if (orderStillValid) {
        setIsCreatingOrder(true);
        try {
          await persistBuyerAndAttendees(orderId);
          const free =
            isFreeCart ||
            existingOrder.payment_method === "free" ||
            Number(existingOrder.total) === 0;
          if (free) {
            await finalizeOrder(orderId, true);
          } else {
            setStep(2);
          }
        } catch (error) {
          console.error("[Checkout] Falha ao re-sincronizar pedido existente:", error);
          toast({
            title: t("checkout.toastsAndErrors.errorUpdatingOrder"),
            description: error.message || t("checkout.toastsAndErrors.tryAgain"),
            variant: "destructive",
          });
        } finally {
          setIsCreatingOrder(false);
        }
        return;
      }
      // Order invalid — clear and create a new one
      sessionStorage.removeItem(orderStorageKey);
      setOrderId(null);
    }
    setIsCreatingOrder(true);
    try {
      const eventId = items[0]?.eventId;
      if (!eventId) throw new Error("No event in cart");

      // Resolve promoter_event_id from tracking code
      let promoterEventId: string | null = null;
      if (trackingCode) {
        const { data: pe } = await supabase
          .from("promoter_events")
          .select("id")
          .eq("tracking_code", trackingCode)
          .eq("event_id", eventId)
          .eq("is_active", true)
          .maybeSingle();
        if (pe) promoterEventId = pe.id;
      }

      // Get ticket tier IDs and quantities (exclude products)
      const ticketItems = items.filter((i) => !i.tierId.startsWith("product-"));
      const ticketTierIds = ticketItems.map((item) => item.tierId);
      const tierIds = ticketItems.map((i) => i.tierId);
      const quantities = ticketItems.map((i) => i.quantity);

      // Validate questions strictly before RPC execution
      const visibleOrderQuestions = orderQuestions.filter((q) => isQuestionAvailableForTiers(q, ticketTierIds));
      for (const q of visibleOrderQuestions) {
        const key = `order-${q.id}`;
        const answer = questionAnswers[key];
        const err = validateQuestionOptions(q, answer);
        if (err) {
          toast({ title: t("checkout.toastsAndErrors.attention"), description: t("checkout.toastsAndErrors.answerInvalid", { question: q.question }), variant: "destructive" });
          setIsCreatingOrder(false);
          return;
        }
      }

      for (const item of ticketItems) {
        for (let qi = 0; qi < item.quantity; qi++) {
          const relevantAttendeeQuestions = attendeeQuestions.filter((q) => isQuestionAvailableForTiers(q, [item.tierId]));
          for (const q of relevantAttendeeQuestions) {
            const key = `attendee-${item.tierId}-${qi}-${q.id}`;
            const answer = questionAnswers[key];
            const err = validateQuestionOptions(q, answer);
            if (err) {
              toast({ title: t("checkout.toastsAndErrors.attention"), description: t("checkout.toastsAndErrors.answerInvalidTicket", { question: q.question, tierName: item.tierName }), variant: "destructive" });
              setIsCreatingOrder(false);
              return;
            }
          }
        }
      }

      // Build billing address string
      const billingAddress = [buyerData.street, buyerData.addressNumber, buyerData.complement, buyerData.neighborhood, buyerData.city, buyerData.state, buyerData.cep].filter(Boolean).join(", ");
      const orderDebugContext = {
        eventId,
        buyerId: user.id,
        tierIds,
        quantities,
        couponCode: couponCode?.trim() || null,
        promoterEventId,
        billingAddress,
        isFreeCart,
        itemCount: items.length,
      };

      // Create order via server-side RPC (includes billing_address for free orders)
      const { data: rpcResult, error: rpcErr } = await supabase.rpc("create_order_validated", {
        p_tier_ids: tierIds,
        p_quantities: quantities,
        p_buyer_id: user.id,
        p_coupon_code: couponCode?.trim() || null,
        p_promoter_event_id: promoterEventId,
        p_billing_address: billingAddress,
      });

      if (rpcErr) throw rpcErr;
      const result = rpcResult;
      if (result?.error) {
        console.error("create_order_validated returned application error:", {
          error: result.error,
          context: orderDebugContext,
        });
        toast({ title: t("checkout.toastsAndErrors.errorCreatingOrder"), description: result.error, variant: "destructive" });
        setIsCreatingOrder(false);
        return;
      }

      const newOrderId = result.order_id;
      const isServerFree = result.is_free;

      setOrderId(newOrderId);
      sessionStorage.setItem(orderStorageKey, newOrderId);
      setOrderExpiresAt(isServerFree ? null : new Date(Date.now() + 15 * 60 * 1000).toISOString());

      // Reserve tickets
      for (const item of ticketItems) {
        const { data: reserved, error: reserveErr } = await supabase.rpc("reserve_tickets", {
          p_tier_id: item.tierId, p_quantity: item.quantity, p_order_id: newOrderId,
        });
        if (reserveErr) throw reserveErr;
        if (!reserved) {
          await supabase.from("orders").update({ status: "cancelled" }).eq("id", newOrderId);
          toast({ title: t("checkout.toastsAndErrors.ticketsSoldOut"), description: t("checkout.toastsAndErrors.insufficientTickets", { tierName: item.tierName }), variant: "destructive" });
          setIsCreatingOrder(false);
          return;
        }
      }

      // Buyer + attendees (phone em dígitos; falha se RLS bloquear)
      await persistBuyerAndAttendees(newOrderId);

      // Save order products
      const productItems = items.filter((i) => i.tierId.startsWith("product-"));
      if (productItems.length > 0) {
        await saveOrderProducts(
          productItems.map((i) => ({
            order_id: newOrderId,
            product_id: i.tierId.replace("product-", ""),
            quantity: i.quantity,
            unit_price: i.price,
          }))
        );
      }

      // Save checkout answers (both order-level AND attendee-level)
      const answersToSave: { order_id: string; question_id: string; answer: string; ticket_id?: string }[] = [];
      const _visibleOrderQuestions = orderQuestions.filter((question) => isQuestionAvailableForTiers(question, ticketTierIds));
      for (const question of _visibleOrderQuestions) {
        const key = `order-${question.id}`;
        const answer = questionAnswers[key];
        if (!answer?.trim()) continue;
        answersToSave.push({ order_id: newOrderId, question_id: question.id, answer });
      }

      const { data: ticketsForAnswers } = await supabase
        .from("tickets")
        .select("id, tier_id")
        .eq("order_id", newOrderId)
        .order("created_at");
      if (ticketsForAnswers && ticketsForAnswers.length > 0) {
        const tierIdx2: Record<string, number> = {};
        for (const ticket of ticketsForAnswers) {
          const idx = tierIdx2[ticket.tier_id] || 0;
          tierIdx2[ticket.tier_id] = idx + 1;
          const keyPrefix = `attendee-${ticket.tier_id}-${idx}`;
          const relevantQuestions = attendeeQuestions.filter((question) => isQuestionAvailableForTiers(question, [ticket.tier_id]));
          for (const question of relevantQuestions) {
            const key = `${keyPrefix}-${question.id}`;
            const answer = questionAnswers[key];
            if (!answer?.trim()) continue;
            answersToSave.push({ order_id: newOrderId, question_id: question.id, answer, ticket_id: ticket.id });
          }
        }
      }

      if (answersToSave.length > 0) {
        await supabase.from("checkout_answers").insert(answersToSave);
      }

      // Meia-entrada com comprovação obrigatória: pedir documentos antes de prosseguir
      const { data: hpRows } = await supabase
        .from("tickets")
        .select("id, attendee_name, ticket_tiers!inner(name, require_document)")
        .eq("order_id", newOrderId)
        .eq("is_half_price", true)
        .eq("ticket_tiers.require_document", true);

      if (hpRows && hpRows.length > 0) {
        setHalfPriceTickets(hpRows.map((t) => ({
          id: t.id,
          eventId,
          tierName: t.ticket_tiers?.name || "Ingresso",
          attendeeName: t.attendee_name,
        })));
        setPendingFinalize(() => () => finalizeOrder(newOrderId, isServerFree));
        setShowHalfPriceDocs(true);
        setIsCreatingOrder(false);
        return;
      }

      await finalizeOrder(newOrderId, isServerFree);
    } catch (error) {
      console.error("Order creation error:", {
        error,
        context: {
          eventId: items[0]?.eventId,
          buyerId: user?.id ?? null,
          tierCount: items.length,
          isFreeCart,
          couponCode: couponCode?.trim() || null,
          trackingCode: trackingCode || null,
        },
      });
      toast({ title: t("checkout.toastsAndErrors.errorCreatingOrder"), description: error.message || t("checkout.toastsAndErrors.tryAgain"), variant: "destructive" });
    } finally {
      setIsCreatingOrder(false);
    }
  }, [user, items, attendeeData, questionAnswers, orderId, couponCode, trackingCode, buyerData, isFreeCart, finalizeOrder, persistBuyerAndAttendees, orderStorageKey, orderQuestions, attendeeQuestions, t]);

  // Process payment
  const handleConfirmPayment = useCallback(async (method: string, cardData?: CreditCardData, installments?: number) => {
    if (!orderId) return;

    setIsProcessingPayment(true);
    try {
      const cleanPhone = normalizePhoneDigits(buyerData.phone);
      // Profile + order (dígitos) + body create-payment — não depender de uma fonte só
      if (user && cleanPhone) {
        const { error: phoneErr } = await supabase
          .from("profiles")
          .update({
            phone: cleanPhone,
            ...(buyerData.fullName?.trim() ? { full_name: buyerData.fullName.trim() } : {}),
          })
          .eq("id", user.id);
        if (phoneErr) {
          console.error("[Checkout] Falha ao persistir telefone no profile:", phoneErr);
          toast({
            title: t("checkout.toastsAndErrors.phoneSaveErrorTitle"),
            description: t("checkout.toastsAndErrors.phoneSaveErrorDesc"),
            variant: "destructive",
          });
          setIsProcessingPayment(false);
          return;
        }
        const { error: orderPhoneErr } = await supabase
          .from("orders")
          .update({
            buyer_phone: cleanPhone,
            ...(buyerData.fullName?.trim() ? { buyer_name: buyerData.fullName.trim() } : {}),
          })
          .eq("id", orderId);
        if (orderPhoneErr) {
          console.error("[Checkout] Falha ao persistir telefone no order:", orderPhoneErr);
          // Não bloqueia: body ainda leva payerPhone
        }
      }
      if (method === "credit_card" && cleanPhone.length < 10) {
        toast({
          title: t("checkout.toastsAndErrors.phoneRequired"),
          description: t("checkout.toastsAndErrors.phoneRequiredDesc"),
          variant: "destructive",
        });
        setIsProcessingPayment(false);
        return;
      }
      const docType = buyerData.cpf.replace(/\D/g, "").length === 14 ? "cnpj" : "cpf";
      const result = await createPayment(
        orderId,
        method as "pix" | "credit_card" | "boleto",
        cardData,
        installments,
        undefined,
        buyerData.cpf,
        docType,
        buyerData.companyName,
        cleanPhone || undefined,
        buyerData.fullName?.trim() || undefined,
      );
      if (!result.success) {
        toast({ title: t("checkout.toastsAndErrors.paymentError"), description: result.error || t("checkout.toastsAndErrors.tryAgain"), variant: "destructive" });
        setIsProcessingPayment(false);
        return;
      }
      setPaymentCreated(true);
      // Always save boleto/barcode data if present in response
      if (result.boletoUrl) setBoletoUrl(result.boletoUrl);
      if (result.boletoBarcode) setBoletoBarcode(result.boletoBarcode);
      if (result.pixQrCode) setPixQrCode(result.pixQrCode);
      if (result.pixQrCodeImage) setPixQrCodeImage(result.pixQrCodeImage);

      if (result.immediateConfirmation) {
        toast({ title: t("checkout.toastsAndErrors.paymentConfirmed"), description: t("checkout.toastsAndErrors.ticketsGeneratedSuccess") });
        clearCart();
        setStep(3);
        sessionStorage.removeItem(orderStorageKey);
      } else {
        setAwaitingPayment(true);
      }
      // Note: stub mode (testing without Asaas) returns immediateConfirmation=true
      // and is already handled by the block above. No separate handling needed.
    } catch (error) {
      console.error("Payment error:", error);
      toast({ title: t("checkout.toastsAndErrors.paymentError"), description: error.message || t("checkout.toastsAndErrors.tryAgain"), variant: "destructive" });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [orderId, clearCart, buyerData.cpf, buyerData.companyName, buyerData.phone, buyerData.fullName, user, orderStorageKey, t]);

  if (items.length === 0 && step < 3) {
    return <Navigate to="/carrinho" replace />;
  }

  if (isRestoringOrder) {
    return (
      <div className="container pt-4 lg:pt-24 pb-16 flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Build summary sidebar info
  const eventTitle = items[0]?.eventTitle || "";
  const coverImage = items[0]?.coverImageUrl;
  const visibleSteps = isFreeCart ? stepsList.filter((_, i) => i !== 2) : stepsList;
  const visibleStep = isFreeCart && step > 2 ? step - 1 : step;

  return (
    <>
      <SEOHead title="Checkout" description="Finalize sua compra de ingressos no TicketHall." />
      <div className="pb-16">
        {/* Progress header — inside the page, separated from navbar */}
        {step <= 3 && (
          <div className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur shadow-sm">
            <div className="container max-w-5xl mx-auto py-4 px-4">
              <div className="flex items-center justify-between gap-4">
                <Link to="/carrinho" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground shrink-0">
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Voltar ao carrinho</span>
                </Link>

                {/* Step bar */}
                <div className="flex items-center gap-0 flex-1 justify-center max-w-sm mx-auto">
                  {visibleSteps.map((s, i) => {
                    const isCompleted = i < visibleStep;
                    const isActive = i === visibleStep;
                    return (
                      <div key={s} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center gap-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 border-2 ${
                            isCompleted ? "bg-primary border-primary text-primary-foreground" :
                            isActive ? "bg-primary border-primary text-primary-foreground" :
                            "bg-background border-border text-muted-foreground"
                          }`}>
                            {isCompleted ? <Check className="h-3.5 w-3.5 stroke-[3]" /> : i + 1}
                          </div>
                          <span className={`text-[10px] font-medium transition-colors whitespace-nowrap hidden sm:block ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                            {s}
                          </span>
                        </div>
                        {i < visibleSteps.length - 1 && (
                          <div className={`h-0.5 flex-1 mx-1 mb-4 sm:mb-[18px] transition-colors duration-500 ${i < visibleStep ? "bg-primary" : "bg-border"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {expiresAt ? (
                  <CountdownTimer expiresAt={expiresAt} onExpire={() => { clearCart(); navigate("/carrinho"); }} />
                ) : (
                  <div className="w-20 shrink-0" />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="container max-w-5xl mx-auto pt-8 px-4">
          <div className="flex flex-col lg:flex-row gap-8 justify-center">
            {/* Main content */}
            <div className="flex-1 max-w-2xl">

              {/* Steps content */}
              {step === 0 && (
                <CheckoutStepBuyer
                  buyerData={buyerData}
                  setBuyerData={setBuyerData}
                  onNext={() => setStep(1)}
                />
              )}

              {step === 1 && showHalfPriceDocs && (
                <CheckoutStepHalfPriceDocs
                  tickets={halfPriceTickets}
                  isFinalizing={isCreatingOrder}
                  onBack={() => setShowHalfPriceDocs(false)}
                  onComplete={async () => {
                    setIsCreatingOrder(true);
                    try {
                      await pendingFinalize?.();
                    } finally {
                      setShowHalfPriceDocs(false);
                      setIsCreatingOrder(false);
                    }
                  }}
                />
              )}

              {step === 1 && !showHalfPriceDocs && (
                <CheckoutStepData
                  items={items}
                  orderQuestions={orderQuestions}
                  attendeeQuestions={attendeeQuestions}
                  attendeeData={attendeeData}
                  setAttendeeData={setAttendeeData}
                  questionAnswers={questionAnswers}
                  setQuestionAnswers={setQuestionAnswers}
                  onNext={handleCreateOrder}
                  onBack={() => setStep(0)}
                  isLoading={isCreatingOrder}
                  buyerData={buyerData}
                />
              )}

              {step === 2 && (
                <>{orderId && eventId && <FiscalCheckoutPanel eventId={eventId} orderId={orderId} onReady={setFiscalReady} payer={buyerData} />}
                <CheckoutStepPayment
                  subtotal={subtotal}
                  platformFee={platformFee}
                  total={finalTotal}
                  onBack={() => setStep(1)}
                  onConfirm={handleConfirmPayment}
                  blockedReason={!fiscalReady ? t('common:nationalFiscal.requiredBeforePayment') : undefined}
                  isProcessing={isProcessingPayment}
                  pixQrCode={pixQrCode}
                  pixQrCodeImage={pixQrCodeImage}
                  boletoUrl={boletoUrl}
                  boletoBarcode={boletoBarcode}
                  paymentCreated={paymentCreated}
                  awaitingPayment={awaitingPayment}
                  expiresAt={orderExpiresAt}
                  payerCpf={buyerData.cpf}
                  onPayerCpfChange={(cpf) => setBuyerData((p) => ({ ...p, cpf }))}
                  companyName={buyerData.companyName}
                  onCompanyNameChange={(companyName) => setBuyerData((p) => ({ ...p, companyName }))}
                  buyerCep={buyerData.cep}
                  buyerAddressNumber={buyerData.addressNumber}
                  onPaymentPreviewChange={setPaymentPreview}
                />
                </>
              )}

              {step === 3 && <CheckoutStepConfirmation orderId={orderId} boletoUrl={boletoUrl} boletoBarcode={boletoBarcode} />}
            </div>

            {/* Order summary sidebar */}
            {step < 3 && (
              <div className="w-full lg:w-[296px] shrink-0">
                <aside className="rounded-xl border border-border bg-card p-[22px] flex flex-col gap-[18px] sticky top-24">
                  <h3 className="font-display text-[15px] font-bold text-foreground m-0">Resumo do pedido</h3>

                  {/* Event info */}
                  {(coverImage || eventTitle) && (
                    <div className="flex gap-3 items-start">
                      {coverImage && (
                        <img src={coverImage} alt={eventTitle} className="w-[52px] h-[52px] rounded-[10px] object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[13px] leading-[1.35] text-foreground mb-1 line-clamp-2">{eventTitle}</p>
                        {eventDetails && (
                          <div className="text-[11px] text-muted-foreground leading-[1.6]">
                            {formatEventDateTimeRange(eventDetails.start_date, null, eventTimeZone, { dateStyle: "long" })}
                            <br />
                            {eventDetails.is_online ? "Evento Online" : (eventDetails.venue_name || eventDetails.venue_city || "Local a definir")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Ticket items */}
                  <div className="border-t border-border pt-[14px] space-y-2">
                    {items.map((item) => (
                      <div key={item.tierId} className="flex justify-between text-[13px]">
                        <span>
                          <span className="font-medium">{item.tierName}</span>
                          <span className="text-muted-foreground ml-1.5">× {item.quantity}</span>
                        </span>
                        <span className="font-semibold">{item.price === 0 ? t("checkout.sidebar.free") : formatBRL(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="border-t border-border pt-[14px] flex flex-col gap-2">
                    <div className="flex justify-between text-[12px]">
                      <span className="text-muted-foreground">{t("checkout.sidebar.subtotal")}</span>
                      <span>{formatBRL(subtotal)}</span>
                    </div>
                    {platformFee > 0 && (
                      <div className="flex justify-between text-[12px]">
                        <span className="text-muted-foreground">{t("checkout.sidebar.serviceFee")}</span>
                        <span>{formatBRL(platformFee)}</span>
                      </div>
                    )}
                    {discount > 0 && (
                      <div className="flex justify-between text-[12px] text-success">
                        <span>{t("checkout.sidebar.discount")}</span>
                        <span>-{formatBRL(discount)}</span>
                      </div>
                    )}
                    {sidebarInterest > 0 && (
                      <div className="flex justify-between text-[12px]">
                        <span className="text-muted-foreground">{t("checkout.sidebar.cardInterest")}</span>
                        <span>{formatBRL(sidebarInterest)}</span>
                      </div>
                    )}
                    {sidebarInstallments > 1 && sidebarInstallmentValue != null && (
                      <div className="flex justify-between text-[11px] text-muted-foreground">
                        <span>{t("checkout.sidebar.installments")}</span>
                        <span>
                          {t("checkout.sidebar.installmentsDetail", { count: sidebarInstallments, value: formatBRL(sidebarInstallmentValue) })}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-[15px] pt-[10px] border-t border-border">
                      <span>{t("checkout.sidebar.total")}</span>
                      <span className={sidebarChargeTotal === 0 ? "text-primary" : "text-foreground"}>
                        {sidebarChargeTotal === 0 ? t("checkout.sidebar.free") : formatBRL(sidebarChargeTotal)}
                      </span>
                    </div>
                  </div>

                  {/* Security */}
                  <div className="border-t border-border pt-[2px] flex flex-col gap-2.5">
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      Pagamento 100% seguro e criptografado
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {["PIX", "VISA", "MASTER", "ELO", "BOLETO"].map((m) => (
                        <span key={m} className="text-[9px] font-bold tracking-wide text-muted-foreground border border-border rounded-[5px] px-[7px] py-[3px]">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
